import {Box} from '@mui/material';
import axios, {isAxiosError} from 'axios';
import React, {useRef, useState} from 'react';
import {useNavigate, useParams, useSearchParams} from 'react-router-dom';
import {CortileAuthLayout} from './CortileAuthLayout';
import {
  Form,
  PasswordField,
  PrimaryButton,
  StepHeading,
  TextButton,
} from './CortileFormParts';
import {GatherField} from './gatherField';
import {CORTILE, FONT} from './tokens';

type Step = 'reset' | 'done' | 'expired';

const RULES = [
  {text: 'At least 12 characters', test: (v: string) => v.length >= 12},
  {
    text: 'Upper and lower case letters',
    test: (v: string) => /[a-z]/.test(v) && /[A-Z]/.test(v),
  },
  {text: 'A number or symbol', test: (v: string) => /[\d\W_]/.test(v)},
];

const COPY = {
  invite: {submit: 'Create password', done: 'Password created'},
  reset: {submit: 'Update password', done: 'Password updated'},
};

const RuleIcon = ({met}: {met: boolean}) => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    aria-label={met ? 'Met' : 'Not met'}
  >
    {met && (
      <>
        <circle cx="12" cy="12" r="10" fill={CORTILE.slate} />
        <path
          d="M7.5 12.5l3 3 6-6.5"
          fill="none"
          stroke={CORTILE.cream}
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </>
    )}
    {!met && (
      <circle
        cx="12"
        cy="12"
        r="9"
        fill="none"
        stroke={CORTILE.disabled}
        strokeWidth="2"
      />
    )}
  </svg>
);

export const CortileSetPassword = () => {
  const {uid, token} = useParams<{uid: string; token: string}>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isInvite = searchParams.get('invite') === '1';
  const copy = isInvite ? COPY.invite : COPY.reset;
  const field = useRef<GatherField | null>(null);
  const [step, setStep] = useState<Step>('reset');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [confirmTouched, setConfirmTouched] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const matches = confirm.length > 0 && confirm === password;
  const rules = [
    ...RULES.map(rule => ({text: rule.text, met: rule.test(password)})),
    {text: 'Both passwords match', met: matches},
  ];
  const valid = rules.every(rule => rule.met);
  const mismatch =
    confirm.length > 0 &&
    !matches &&
    (confirmTouched || confirm.length >= password.length);

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!valid) return;
    setError(null);
    setSubmitting(true);
    try {
      await axios.post(
        `${import.meta.env.VITE_API_ROOT}/auth/users/reset_password_confirm/`,
        {uid, token, new_password: password},
      );
      setPassword('');
      setConfirm('');
      setStep('done');
      field.current?.celebrate();
    } catch (e) {
      const data = isAxiosError(e) ? e.response?.data : undefined;
      if (data?.uid || data?.token) {
        setStep('expired');
      } else {
        setError(data?.new_password?.[0] ?? 'Something went wrong');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <CortileAuthLayout stepKey={step} fieldRef={field}>
      {step === 'reset' && (
        <>
          {isInvite && (
            <StepHeading title="Create your password">
              Welcome to Cortile. Choose a password to finish setting up your
              account.
            </StepHeading>
          )}
          {!isInvite && <StepHeading title="Set a new password" />}
          <Form onSubmit={onSubmit}>
            <PasswordField
              label="New password"
              data-attr="cortile-login-new-password-input"
              autoComplete="new-password"
              placeholder="Create a password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              error={error ?? undefined}
            />
            <PasswordField
              label="Confirm new password"
              data-attr="cortile-login-confirm-password-input"
              autoComplete="new-password"
              placeholder="Enter it again"
              value={confirm}
              onChange={e => setConfirm(e.target.value)}
              onBlur={() => setConfirmTouched(true)}
              error={mismatch ? "Passwords don't match" : undefined}
            />
            <Box
              aria-live="polite"
              sx={{display: 'flex', flexDirection: 'column', gap: 1.25}}
            >
              {rules.map(rule => (
                <Box
                  key={rule.text}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.25,
                    fontFamily: FONT,
                    fontSize: 14,
                    color: rule.met ? CORTILE.slate : CORTILE.muted,
                    fontWeight: rule.met ? 600 : 400,
                  }}
                >
                  <RuleIcon met={rule.met} />
                  <span>{rule.text}</span>
                </Box>
              ))}
            </Box>
            <PrimaryButton
              type="submit"
              disabled={!valid}
              loading={submitting}
              data-attr="cortile-login-set-password-submit"
            >
              {copy.submit}
            </PrimaryButton>
          </Form>
        </>
      )}
      {step === 'done' && (
        <>
          <StepHeading title={copy.done}>
            You can now log in with your new password.
          </StepHeading>
          <PrimaryButton
            type="button"
            onClick={() => navigate('/login')}
            data-attr="cortile-login-set-password-done"
          >
            Log in
          </PrimaryButton>
        </>
      )}
      {step === 'expired' && isInvite && (
        <>
          <StepHeading title="This invite has expired">
            Invite links expire after a while. Ask your manager or admin to
            resend your invite, then follow the link in the new email.
          </StepHeading>
          <TextButton
            back
            onClick={() => navigate('/login')}
            data-attr="cortile-login-set-password-back"
          >
            Back to log in
          </TextButton>
        </>
      )}
      {step === 'expired' && !isInvite && (
        <>
          <StepHeading title="This link has expired">
            Reset links can only be used once and expire after a while. Request
            a new one and we&rsquo;ll email it to you.
          </StepHeading>
          <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
            <PrimaryButton
              type="button"
              onClick={() => navigate('/reset-password')}
              data-attr="cortile-login-set-password-resend"
            >
              Send a new link
            </PrimaryButton>
            <TextButton
              back
              onClick={() => navigate('/login')}
              data-attr="cortile-login-set-password-back"
            >
              Back to log in
            </TextButton>
          </Box>
        </>
      )}
    </CortileAuthLayout>
  );
};
